import React, { useCallback, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ConstructionIcon from '@mui/icons-material/Construction';
import { Link } from 'react-router-dom';
import { reportsApi } from '../../services/mybudgetApi';
import { DashboardSummary } from '../../types';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { CategoryBadge } from '../../components/shared/CategoryBadge';
import { formatCurrency, formatMonthYear, formatPercent, MONTHS } from '../../constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

function ChangeIndicator({ value, invert }: { value: number; invert?: boolean }) {
  const isPositive = invert ? value < 0 : value > 0;
  const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;
  const color = isPositive ? 'text-income' : 'text-expense';
  return (
    <span className={`flex items-center gap-1 text-xs ${color}`}>
      <Icon fontSize="inherit" />
      {formatPercent(value)} vs mês anterior
    </span>
  );
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await reportsApi.dashboard());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loading />;
  if (!data) return null;

  const { current, previous, incomeChange, expenseChange, last6Months, projects } = data;

  const chartData = {
    labels: last6Months.map((m) => MONTHS[m.month - 1].slice(0, 3)),
    datasets: [
      {
        label: 'Receitas',
        data: last6Months.map((m) => m.totalIncome),
        backgroundColor: 'rgba(52, 211, 153, 0.7)',
        borderColor: '#34d399',
        borderRadius: 6,
      },
      {
        label: 'Despesas',
        data: last6Months.map((m) => m.totalExpenses),
        backgroundColor: 'rgba(248, 113, 113, 0.6)',
        borderColor: '#f87171',
        borderRadius: 6,
      },
    ],
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-100">Dashboard</h2>
        <p className="text-sm text-gray-400">
          {formatMonthYear(current.year, current.month)} — visão geral
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card glow="income">
          <p className="text-sm text-gray-400">Receitas</p>
          <p className="mt-2 text-2xl font-bold text-income">
            {formatCurrency(current.totalIncome)}
          </p>
          <ChangeIndicator value={incomeChange} />
        </Card>
        <Card glow="expense">
          <p className="text-sm text-gray-400">Despesas</p>
          <p className="mt-2 text-2xl font-bold text-expense">
            {formatCurrency(current.totalExpenses)}
          </p>
          <ChangeIndicator value={expenseChange} invert />
        </Card>
        <Card>
          <p className="text-sm text-gray-400">Saldo</p>
          <p
            className={`mt-2 text-2xl font-bold ${current.balance >= 0 ? 'text-income' : 'text-expense'}`}
          >
            {formatCurrency(current.balance)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Anterior: {formatCurrency(previous.balance)}
          </p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-gray-400">
            <AccountBalanceIcon fontSize="small" />
            <p className="text-sm">Projetado</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-amber-400">
            {formatCurrency(current.totalProjected)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {formatCurrency(current.totalActual)} confirmadas
          </p>
        </Card>
      </div>

      {projects.activeCount > 0 && (
        <Card
          title="Projetos em aberto"
          action={
            <Link to="/projects" className="text-sm text-link hover:underline">
              Ver todos
            </Link>
          }
        >
          <div className="mb-4 flex items-center gap-3">
            <ConstructionIcon className="text-amber-400" />
            <div>
              <p className="text-sm text-gray-400">Saldo devedor total</p>
              <p className="text-xl font-bold text-amber-400">
                {formatCurrency(projects.totalOutstanding)}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {projects.projects.slice(0, 3).map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between rounded-xl border border-dark-border bg-dark-bg/40 px-4 py-3"
              >
                <div>
                  <p className="font-medium text-gray-100">{p.name}</p>
                  <p className="text-xs text-gray-500">
                    {formatCurrency(p.paidAmount)} de {formatCurrency(p.totalAmount)}
                  </p>
                </div>
                <p className="font-semibold text-amber-400">
                  {formatCurrency(p.remainingAmount)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Últimos 6 meses" className="lg:col-span-2">
          <Bar
            data={chartData}
            options={{
              responsive: true,
              plugins: { legend: { labels: { color: '#9ca3af' } } },
              scales: {
                y: {
                  ticks: { color: '#9ca3af' },
                  grid: { color: '#243044' },
                },
                x: {
                  ticks: { color: '#9ca3af' },
                  grid: { color: '#243044' },
                },
              },
            }}
          />
        </Card>

        <Card title="Por categoria">
          <div className="space-y-3">
            {current.categoryBreakdown.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma despesa neste mês</p>
            ) : (
              current.categoryBreakdown.map((c) => (
                <div key={c.categoryId} className="flex items-center justify-between">
                  <CategoryBadge category={c.category} />
                  <span className="text-sm font-medium text-gray-200">
                    {formatCurrency(c.total)}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
